import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * TiltedScreen - wraps any child in a 3D parallax tilt that follows the
 * mouse cursor. Used to make ProductScreen mockups feel like real, physical
 * objects floating in the layout. Pure CSS transform, GPU-accelerated.
 */
export default function TiltedScreen({ children, maxTilt = 8, className = "" }) {
  const ref = useRef(null);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth spring physics so the tilt eases rather than snaps.
  const springConfig = { stiffness: 120, damping: 18, mass: 0.4 };
  const sx = useSpring(mouseX, springConfig);
  const sy = useSpring(mouseY, springConfig);

  const rotateX = useTransform(sy, [-0.5, 0.5], [maxTilt, -maxTilt]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-maxTilt, maxTilt]);

  // Spotlight gradient overlay follows the cursor too.
  const spotX = useTransform(sx, [-0.5, 0.5], ["20%", "80%"]);
  const spotY = useTransform(sy, [-0.5, 0.5], ["20%", "80%"]);

  const onMouseMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const onMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className={`relative [perspective:1400px] ${className}`}
      data-testid="tilted-screen"
    >
      <motion.div
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
        }}
        className="relative will-change-transform"
      >
        {children}
        {/* Holographic spotlight overlay */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-60"
          style={{
            background: useTransform(
              [spotX, spotY],
              ([x, y]) =>
                `radial-gradient(circle at ${x} ${y}, rgba(255,255,255,0.18) 0%, transparent 45%)`,
            ),
          }}
        />
      </motion.div>
    </div>
  );
}
